import { useStore } from '@/store';
import { computed } from 'vue';
import { PLAN_IDS, PLAN_NAMES } from '../constants';
import useLibrary from '@modules/libraries/composable/useLibrary';
import eventService, { EVENT_TYPES } from '@/services/EventService';
import Api from '@/services/Api';
import { base } from '@/services';
import { PAGE_TYPES } from '../constants';
import { mutations as libraryMutations } from '@/modules/libraries/store/types';

export const usePlanPermission = () => {
  const store = useStore();

  const { submitEvent } = eventService();
  const { numberUseAddToPage } = useLibrary();

  const OLD_PLAN_IDS = [PLAN_IDS.STARTER, PLAN_IDS.PROFESSIONAL, PLAN_IDS.ADVANCE];
  const shopInfo = computed(() => store.state.app?.shopInfo);
  const isEnterprisePlan = computed(() => shopInfo.value.plan === PLAN_NAMES.ENTERPRISE);
  const isAdvancePlan = computed(() => shopInfo.value.plan === PLAN_NAMES.ADVANCE);
  const pageInfo = computed(() => store.state.app?.pageInfo);
  const pageType = computed(() => store.state.app?.pageInfo?.page?.type);
  const planNo = computed(() => shopInfo.value.plan);

  const publishedPageInfo = computed(() => {
    const limitationOfPlan = shopInfo.value?.planLimitation[planNo.value];
    let fieldName = '';
    let fieldNamePublished = '';
    switch (pageType.value) {
      case PAGE_TYPES.ARTICLE_PAGE:
        fieldName = 'nbOfPublishedArticlePage';
        fieldNamePublished = 'publishedArticlePagesCount';
        break;
      case PAGE_TYPES.HOME_PAGE:
        fieldName = 'nbOfPublishedIndexPage';
        break;
      case PAGE_TYPES.COLLECTION_PAGE:
        fieldName = 'nbOfPublishedCollectionPage';
        break;
      case PAGE_TYPES.LANDING_PAGE:
        fieldName = 'nbOfPublishedLandingPage';
        break;
      case PAGE_TYPES.PRODUCT_PAGE:
        fieldName = 'nbOfPublishedProductPage';
        fieldNamePublished = 'publishedProductPagesCount';
        break;
      default:
        break;
    }

    const limitedPublishPageByType = (limitationOfPlan?.pagePublish || {})[fieldName] || 0;
    const limitedPublishPage = (limitationOfPlan?.pagePublish || {}).nbOfPublishedPage || 0;
    const publishedPage = shopInfo.value.publishedPagesCount;
    const publishedPageByType = shopInfo.value[fieldNamePublished] || 0;

    return {
      limitedPublishPageByType,
      limitedPublishPage,
      publishedPage,
      publishedPageByType,
    };
  });

  const limitedPublished = computed(() => {
    const isLimitPublish = pageInfo.value.isLimitPublish;
    if (isLimitPublish !== undefined) {
      return isLimitPublish;
    }

    const limitationOfPlan = shopInfo.value?.planLimitation[planNo.value];
    let fieldName = '';
    let fieldNamePublished = '';
    switch (pageType.value) {
      case PAGE_TYPES.ARTICLE_PAGE:
        fieldName = 'nbOfPublishedArticlePage';
        fieldNamePublished = 'publishedArticlePagesCount';
        break;
      case PAGE_TYPES.HOME_PAGE:
        fieldName = 'nbOfPublishedIndexPage';
        break;
      case PAGE_TYPES.COLLECTION_PAGE:
        fieldName = 'nbOfPublishedCollectionPage';
        break;
      case PAGE_TYPES.LANDING_PAGE:
        fieldName = 'nbOfPublishedLandingPage';
        break;
      case PAGE_TYPES.PRODUCT_PAGE:
        fieldName = 'nbOfPublishedProductPage';
        fieldNamePublished = 'publishedProductPagesCount';
        break;
      default:
        break;
    }

    const limitedPublishPageByType = (limitationOfPlan?.pagePublish || {})[fieldName] || 0;
    const limitedPublishPage = (limitationOfPlan?.pagePublish || {}).nbOfPublishedPage || 0;
    const publishedPage = shopInfo.value.publishedPagesCount;
    const publishedPageByType = shopInfo.value[fieldNamePublished] || 0;

    if (planNo.value === PLAN_NAMES.TRIAL_2022 || planNo.value === PLAN_NAMES.TRIAL) {
      return publishedPage >= limitedPublishPage;
    }

    return !(limitedPublishPageByType > publishedPageByType && limitedPublishPage > publishedPage);
  });

  const limitSaveAndExportToLibrary = computed(() => {
    const limitationOfPlan = shopInfo.value?.planLimitation[planNo.value];

    if (limitationOfPlan) {
      return limitationOfPlan.keyFeatures?.exportLibrary;
    }
    return null;
  });

  const isTrialExpired = computed(() => {
    return (
      (shopInfo.value.plan === PLAN_NAMES.TRIAL || shopInfo.value.plan === PLAN_NAMES.TRIAL_2022) &&
      new Date().valueOf() > new Date(shopInfo.value?.endTrialDate).valueOf()
    );
  });

  const isTrial = computed(
    () => shopInfo.value.plan === PLAN_NAMES.TRIAL || shopInfo.value.plan === PLAN_NAMES.TRIAL_2022,
  );
  const isBuildPlan = computed(() => shopInfo.value.plan === PLAN_NAMES.BUILD);
  const lazyLoadingPlanNotShow = computed(() => !store.state.app?.planPermissions?.lazyLoad);

  const checkValidSaveToLibrary = async () => {
    const permission = getPermission();

    return new Promise(async (resolve, reject) => {
      if (!permission.saveToLibrary) {
        return resolve(false);
      }
      if (!limitSaveAndExportToLibrary.value) {
        return resolve(true);
      }
      try {
        // GET
        const { nbOfExportLibrary } = limitSaveAndExportToLibrary.value || { nbOfExportLibrary: 1 };
        const res = await Api.base.get(base.getMyLibrary, {
          params: { type: 'all', offset: 0, counter: true },
        });
        const { total } = res.data;
        store.commit(`library/${libraryMutations.COMMIT_SAVED_LIBRARY_INFO}`, total);
        return resolve(total.block + total.template < nbOfExportLibrary);
      } catch (e) {
        reject(e);
      }
    });
  };

  const getPlanInfo = () => {
    return {
      planName: shopInfo.value?.plan,
      planId: shopInfo.value?.planId,
    };
  };

  const getPermission = () => {
    return store.state.app?.planPermissions;
  };

  const isOldPlan = () => {
    const plan = getPlanInfo();
    return OLD_PLAN_IDS.includes(plan.planId);
  };

  const getPermissionForTypeInfo = (permissionType: string) => {
    return store.state.app?.planPermissions[permissionType] || false;
  };

  const insertBlockTemplate = computed(() => {
    return shopInfo.value?.planLimitation[planNo.value]?.keyFeatures?.insertBlockTemplate;
  });

  const limitInsertElement = computed(() => {
    return (
      insertBlockTemplate.value?.enabled && numberUseAddToPage.value < insertBlockTemplate.value?.nbOfBlockTemplate
    );
  });

  const submitEventSaveToLibrary = async () => {
    if (isAdvancePlan.value || isEnterprisePlan.value) return;

    submitEvent({
      event_type: EVENT_TYPES.saveToLibrary,
      shopify_domain: shopInfo.value.shopifyDomain,
    });
  };

  return {
    getPlanInfo,
    getPermission,
    getPermissionForTypeInfo,
    isOldPlan,
    shopInfo,
    isTrial,
    isBuildPlan,
    pageInfo,
    limitInsertElement,
    lazyLoadingPlanNotShow,
    checkValidSaveToLibrary,
    submitEventSaveToLibrary,
    isTrialExpired,
    limitedPublished,
    pageType,
    publishedPageInfo,
  };
};

export default usePlanPermission;
